import React from "react";

interface PaginationContextValue {
  firstRow: number;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  initialize: () => void;
  isAtFirstPage: boolean;
  isAtLastPage: boolean;
  lastRow: number;
  pageSize: number | null;
  pageSizes: (number | null)[];
  setPageSize: (pageSize: number | null) => void;
  totalRows: number;
}

export const PaginationContext = React.createContext<PaginationContextValue>({
  firstRow: 0,
  goToNextPage: () => {},
  goToPreviousPage: () => {},
  initialize: () => {},
  isAtFirstPage: true,
  isAtLastPage: true,
  lastRow: 0,
  pageSize: null,
  pageSizes: [],
  setPageSize: () => {},
  totalRows: 0,
});

export const PaginationProvider: React.FC<{
  children: React.ReactNode;
  currentPageIndex: number;
  firstRowIndex: number;
  lastRowIndex: number;
  onGoToNextPage: () => void;
  onGoToPreviousPage: () => void;
  onInitialize: () => void;
  onSetPageSize: (pageSize: number | null) => void;
  pageSize: number | null;
  pageSizes: (number | null)[];
  scrollTargetRef?: React.RefObject<HTMLElement>;
  totalPages: number;
  totalRows: number;
}> = (props) => {
  const isScrolling = React.useRef<boolean>(false);
  const isScrollAbandoned = React.useRef<boolean>(false);

  const abandonScroll = React.useCallback(() => {
    isScrollAbandoned.current = true;
  }, []);

  const scrollToTop = React.useCallback(() => {
    const target = props.scrollTargetRef?.current;
    if (!target) {
      return;
    }

    const offset = target.getBoundingClientRect().top;
    if (offset < 0 && !isScrollAbandoned.current) {
      if (!isScrolling.current) {
        isScrolling.current = true;
        window.addEventListener("keydown", abandonScroll);
        window.addEventListener("mousedown", abandonScroll);
        window.addEventListener("touchstart", abandonScroll);
        window.addEventListener("wheel", abandonScroll);
      }

      window.scrollBy(0, -Math.max((-1 * offset) / 10, 5));
      requestAnimationFrame(() => scrollToTop());
    } else if (isScrolling.current) {
      isScrolling.current = false;
      isScrollAbandoned.current = false;
      window.removeEventListener("keydown", abandonScroll);
      window.removeEventListener("mousedown", abandonScroll);
      window.removeEventListener("touchstart", abandonScroll);
      window.removeEventListener("wheel", abandonScroll);
    }
  }, [props.scrollTargetRef]);

  const goToNextPage = React.useCallback(() => {
    props.onGoToNextPage();
    scrollToTop();
  }, [props.onGoToNextPage]);

  const goToPreviousPage = React.useCallback(() => {
    props.onGoToPreviousPage();
    scrollToTop();
  }, [props.onGoToPreviousPage]);

  return (
    <PaginationContext.Provider
      value={{
        firstRow: Math.min(props.firstRowIndex + 1, props.totalRows),
        goToNextPage: goToNextPage,
        goToPreviousPage: goToPreviousPage,
        initialize: props.onInitialize,
        isAtFirstPage: props.currentPageIndex === 0,
        isAtLastPage:
          props.currentPageIndex === Math.max(props.totalPages - 1, 0),
        lastRow: props.lastRowIndex + 1,
        pageSize: props.pageSize,
        pageSizes: props.pageSizes,
        setPageSize: props.onSetPageSize,
        totalRows: props.totalRows,
      }}
    >
      {props.children}
    </PaginationContext.Provider>
  );
};
